import { designTokens } from "@itandi/itandi-bb-ui";
import { Box } from "@itandi/itandi-bb-ui/Box";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { Panel } from "@itandi/itandi-bb-ui/Panel";
import { PanelBody } from "@itandi/itandi-bb-ui/PanelBody";
import { PanelHeader } from "@itandi/itandi-bb-ui/PanelHeader";
import { useState } from "react";
import { CompanyVendorSelector } from "~/components/common/company_vendors/CompanyVendorSelector";
import { DealCancelButtonClient } from "~/components/common/deals/show/main/DealCancelButtonClient";
import { DealMainWorkflowClientCancel } from "~/components/common/deals/show/main/DealMainWorkflowClientCancel";
import { DealClientTodoModal } from "~/components/common/deals/show/main/todos/DealClientTodoModal";
import { DealClientTodoTable } from "~/components/common/deals/show/main/todos/DealClientTodoTable";
import { DealWorkflowTable } from "~/components/common/deals/show/main/workflows/DealWorkflowTable";
import { StatusBar } from "~/components/common/parts/status/StatusBar";
import { DealSurveyAnswers } from "~/components/specifics/deals/[id]/deal_survey/Answers";
import { StatusSwitcher } from "~/components/specifics/deals/[id]/statuses/StatusSwitcher";
import { DealContext } from "~/context/DealContext";
import { useDealSurveyAnswers } from "~/hooks/deals/useDealSurveyAnswers";
import { useDeals } from "~/hooks/useDeals";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import { useEnabledStaffs } from "~/hooks/useStaffs";
import { useStatuses } from "~/hooks/useStatuses";
import { useUsingApplications } from "~/hooks/useUsingApplications";
import { isWithVendorDeal } from "~/models/Deal";
import { getOriginDealVendor } from "~/models/DealVendorCompany";
import {
	getOwnStatuses,
	getWithVendorStatuses,
	isCancelApplying,
	isCanceled,
	isCompleted,
	NOW_ACCEPTING
} from "~/models/Status";
import Styles from "./DealMainWorkflow.module.scss";

export function DealMainWorkflowClient() {
	const id = useIdQueryHandler();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { useItandiTask } = useUsingApplications();
	const { data: deal, mutate: mutateDeal } = useDeals().show(id);
	const staffs = useEnabledStaffs().data;
	const { data: statuses } = useStatuses().index();
	const { data: answers } = useDealSurveyAnswers().index(id);

	if (statuses === null || deal === null) {
		return null;
	}

	const originVendor = getOriginDealVendor(deal);
	const displayStatuses = isWithVendorDeal(deal)
		? getWithVendorStatuses(statuses)
		: getOwnStatuses(statuses);

	if (isCancelApplying(deal)) {
		return (
			<DealMainWorkflowClientCancel
				companyVendor={originVendor?.companyVendor ?? null}
				deal={deal}
				displayStatuses={displayStatuses}
			/>
		);
	}

	return (
		<DealContext.Provider value={{ deal, mutateDeal }}>
			<div>
				{!isCanceled(deal.statusId) && (
					<Box mb={designTokens["spacing/l"]}>
						<Panel>
							<PanelHeader
								rightArea={
									!isCompleted(deal.statusId) && <DealCancelButtonClient />
								}
								title={
									originVendor === null
										? "まだ施工会社が選択されていません"
										: `${originVendor.companyVendor?.name ?? ""}${!isCompleted(deal.statusId) ? "に依頼中" : ""}`
								}
							/>
							<PanelBody>
								<StatusBar
									currentStatusId={deal.statusId}
									statuses={displayStatuses}
								/>
							</PanelBody>
						</Panel>
					</Box>
				)}
				<Box mb={designTokens["spacing/l"]}>
					<Panel>
						<PanelHeader
							rightArea={
								<Buttonv2
									onClick={() => {
										setIsModalOpen(true);
									}}
									variant="primary"
								>
									追加
								</Buttonv2>
							}
							title="TODO"
						/>
						<PanelBody>
							<DealClientTodoTable />
						</PanelBody>
					</Panel>
				</Box>
				{useItandiTask ? (
					<Box mb={designTokens["spacing/l"]}>
						<Panel>
							<PanelHeader title="開始したワークフロー" />
							<PanelBody>
								<DealWorkflowTable />
							</PanelBody>
						</Panel>
					</Box>
				) : null}
				{isModalOpen && staffs !== null ? (
					<DealClientTodoModal
						deal={deal}
						handleClose={() => {
							setIsModalOpen(false);
						}}
						staffs={staffs}
					/>
				) : null}
				{answers !== null && answers.length !== 0 && (
					<div className={Styles.SurveyAnswerCard}>
						<DealSurveyAnswers answers={answers} />
					</div>
				)}
				{deal.statusId === NOW_ACCEPTING && (
					<div className={Styles.SelectDealVendorCompanyCard}>
						<div>
							<h3 className={Styles.Header}>
								工事を依頼する施工会社を選択してください
							</h3>
							<CompanyVendorSelector
								deal={deal}
								key={JSON.stringify({
									dealId: deal.id,
									estimationBy: deal.estimationBy,
									savedCompanyVendorId: deal.dealVendorCompanies[0] ?? null,
									savedShowTenant:
										deal.dealVendorCompanies[0]?.showTenant ?? false
								})}
								mutateDeal={mutateDeal}
							/>
						</div>
					</div>
				)}
				<div className={Styles.WorkflowCard}>
					<StatusSwitcher statusId={deal.statusId} />
				</div>
			</div>
		</DealContext.Provider>
	);
}
